import React, { useRef, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { Paperclip, Trash2, Plus } from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { uploadFilesToBackend } from 'components/FileManagement/hooks/fileMutations';
import { fetchDownloadSignedUrl } from 'components/FileManagement/hooks/useNotebookQueries';
import { WORKSPACE_TYPES } from '../../types/workspace';
import { selectGroupGuid, useAppSelector } from 'slices/commonSelectors';
import { parseError } from 'utils/parseTypedErrorMessage';
import { observability } from 'utils/observability';

/** Managed-file metadata returned by the org file library upload. */
export type AttachmentFile = {
  fileId?: string;
  name: string;
  url: string;
  size?: number;
  mimetype?: string;
};
export type AttachmentLink = { name: string; url: string };

export type ActionAttachmentsFormValues = {
  attachmentFiles?: AttachmentFile[];
  attachmentLinks?: AttachmentLink[];
};

const MAX_FILES_PER_UPLOAD = 10;

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ActionAttachments: React.FC = () => {
  const { control, register } = useFormContext<ActionAttachmentsFormValues>();
  const groupGuid = useAppSelector(selectGroupGuid);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const files = useFieldArray({ control, name: 'attachmentFiles' });
  const links = useFieldArray({ control, name: 'attachmentLinks' });

  // Files go through the same upload path as the Organization Files page
  // (owner = group_guid, scopeType = organization); the action only stores
  // the resulting managed-file metadata.
  const handleFilesSelected = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selected = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (selected.length === 0) return;
    if (selected.length > MAX_FILES_PER_UPLOAD) {
      toast.info(
        `Maximum upload limit is ${MAX_FILES_PER_UPLOAD} files at a time`
      );
      return;
    }
    if (!groupGuid) {
      toast.error('Select an organization before attaching files.');
      return;
    }

    setIsUploading(true);
    try {
      const uploaded = await uploadFilesToBackend({
        files: selected,
        workspaceId: groupGuid,
        scopeType: WORKSPACE_TYPES.ORG,
      });
      files.append(
        uploaded.map((file, index) => {
          const size = Number(file.size);
          return {
            fileId: file.id,
            name: file.file_name ?? selected[index]?.name ?? 'file',
            url: file.fileUrl,
            size: Number.isFinite(size) ? size : selected[index]?.size,
            mimetype: file.mimetype,
          };
        })
      );
    } catch (error) {
      observability.captureException(error);
      toast.error(parseError(error) ?? 'Failed to upload files.');
    } finally {
      setIsUploading(false);
    }
  };

  // Blobs live in a private container, so the stored URL must be signed
  // before the browser can open it.
  const openFile = async (blobUrl: string) => {
    const fileWindow = window.open('', '_blank');
    try {
      const signedUrl = await fetchDownloadSignedUrl(blobUrl);
      if (fileWindow) {
        fileWindow.location.href = signedUrl;
      } else {
        window.open(signedUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      fileWindow?.close();
      observability.captureException(error);
      toast.error(parseError(error) ?? 'Failed to open file.');
    }
  };

  return (
    <div className="mt-3 rounded border border-neutral-500 p-3">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h4 className="tw:m-0 tw:text-xs tw:font-semibold tw:tracking-wide tw:uppercase tw:text-muted-foreground">
          Attachments
        </h4>
        <div className="d-flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isUploading}
            onClick={() => fileInputRef.current?.click()}
          >
            <Paperclip data-icon="inline-start" />
            {isUploading ? 'Uploading…' : 'Add Files'}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => links.append({ name: '', url: '' })}
          >
            <Plus data-icon="inline-start" />
            Add Link
          </Button>
        </div>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        hidden
        aria-label="Add files"
        onChange={handleFilesSelected}
      />
      <p className="tw:text-xs text-weak mb-2">
        Reference an existing document by link, or attach a file from your
        computer.
      </p>

      {files.fields.length === 0 && links.fields.length === 0 && (
        <p className="txt--sml text-weak mb-0">No attachments yet</p>
      )}
      {files.fields.length > 0 && (
        <ul className="list-unstyled mb-2">
          {files.fields.map((field, index) => (
            <li
              key={field.id}
              className="d-flex align-items-center justify-content-between"
            >
              <span>
                <Button
                  type="button"
                  variant="link"
                  className="tw:h-auto tw:p-0"
                  onClick={() => openFile(field.url)}
                >
                  {field.name}
                </Button>
                {typeof field.size === 'number' && (
                  <span className="color-muted">
                    {' '}
                    ({formatSize(field.size)})
                  </span>
                )}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Remove file ${field.name}`}
                onClick={() => files.remove(index)}
              >
                <Trash2 />
              </Button>
            </li>
          ))}
        </ul>
      )}
      {links.fields.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>URL</TableHead>
              <TableHead>
                <span className="visually-hidden">Delete</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {links.fields.map((field, index) => (
              <TableRow key={field.id}>
                <TableCell>
                  <Input
                    aria-label={`Link name ${index + 1}`}
                    {...register(`attachmentLinks.${index}.name`)}
                  />
                </TableCell>
                <TableCell>
                  <Input
                    type="url"
                    placeholder="https://"
                    aria-label={`Link URL ${index + 1}`}
                    {...register(`attachmentLinks.${index}.url`)}
                  />
                </TableCell>
                <TableCell>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Delete link ${index + 1}`}
                    onClick={() => links.remove(index)}
                  >
                    <Trash2 />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

export default ActionAttachments;
